import React from 'react';
import ResourceDetailHeader from 'js/components/ResourceDetailHeader/ResourceDetailHeader';
import { updatePipelineInfo } from 'js/actions';
import { Stack } from 'new/elements/Stack';
import { RESOURCE_TYPE_FLOW } from 'js/constants/resourceTypes';
import styles from './DataFlowDetailHeader.module.scss';
import PipelineStatusChip from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import DataFlowDetailActionsContainer from '../DataFlowDetailActionsContainer';
import ModalCloneFlowContainer from 'js/components/pipelines/ModalCloneFlowContainer';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { isFlowNodeResource } from 'js/lib/ResourceUtils';
import { isCapsuleCluster } from 'js/lib/PipelineUtils';
import { CapsuleClusterChip } from 'new/modules/settings/capsules/components/CapsuleClusterChip/CapsuleClusterChip';
import { useLocation } from 'react-router-dom';
import { isFullScreenCanvasURL } from 'js/lib/URLUtils';
import { useFlowDuplicate } from 'new/hooks/useFlowDuplicate';
import { useAppContext } from 'new/hooks/useAppContext';

interface DataFlowDetailHeaderProps {
  readOnly: boolean;
  adminAccess: boolean;
  hideActions: boolean;
  data: any;
  onCloseDrawer?(): void;
}

const Connection = ResourceDetailHeader.TopContent.Connection;

export const DataFlowDetailHeader = (props: DataFlowDetailHeaderProps) => {
  const { readOnly, hideActions, adminAccess, data } = props;

  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const isFullScreenCanvas = isFullScreenCanvasURL(pathname);
  const { isEmbeddedMode } = useAppContext();
  const { cloneFlowData, toggleCloneFlowModal, handleDuplicateSuccess } = useFlowDuplicate();

  if (!data) return null;

  const isFlowNode = isFlowNodeResource(data);
  const flowOrNodeId = data?.flow_node_id ?? (isFlowNode ? data?.id : undefined);
  const updateInfo = (sourceID: number, info = {}) => {
    dispatch(updatePipelineInfo(flowOrNodeId, info));
  };

  return (
    <ResourceDetailHeader
      readOnly={readOnly}
      adminAccess={adminAccess}
      data={data}
      updateInfo={updateInfo}
      onCloseDrawer={props?.onCloseDrawer}
    >
      <ResourceDetailHeader.TopContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap="16px">
          <Stack direction="row" alignItems="center" gap="16px">
            <Connection resourceType={RESOURCE_TYPE_FLOW} data={data} />
            <ChipWithCopy tooltipTitle="Copy Data Flow ID" toCopy={flowOrNodeId}>
              ID {flowOrNodeId}
            </ChipWithCopy>
            {isCapsuleCluster(data) && <CapsuleClusterChip name={data.cluster.name} />}
            <div className={styles.boxInfo}>
              <PipelineStatusChip
                id={flowOrNodeId ?? ''}
                status={data.status}
                archivalStatus={data?.archival_status}
                showRuntimeIndicator={false}
              />
            </div>
          </Stack>
          <Stack direction="row" alignItems="center" gap="16px">
            {!hideActions && (
              <DataFlowDetailActionsContainer
                data={data}
                projectId={data?.project_id}
                hideZoomIcon={true}
                toggleCloneFlowModal={toggleCloneFlowModal}
                hideMenuDetails={true}
                isExpandedCanvas={isFullScreenCanvas}
                embedded={isEmbeddedMode}
              />
            )}
          </Stack>
        </Stack>
        {cloneFlowData && (
          <ModalCloneFlowContainer
            data={cloneFlowData}
            toggleCloneFlowModal={toggleCloneFlowModal}
            setSelectedPipeline={isFullScreenCanvas ? handleDuplicateSuccess : undefined}
          />
        )}
      </ResourceDetailHeader.TopContent>
    </ResourceDetailHeader>
  );
};
